import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI, eventsAPI, contactAPI } from '../../services/api';
import { Package, Calendar, Mail, Plus, TrendingUp } from 'lucide-react';

const Dashboard = () => {
    const [stats, setStats] = useState({
        products: 0,
        events: 0,
        contacts: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [productsRes, eventsRes] = await Promise.all([
                    productsAPI.getAll(),
                    eventsAPI.getAll(),
                ]);

                setStats({
                    products: productsRes.data.data.length,
                    events: eventsRes.data.data.length,
                    contacts: 0, // Will be updated when we fetch contacts
                });
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    const statCards = [
        {
            title: 'Total Products',
            value: stats.products,
            icon: Package,
            color: 'bg-blue-500',
            link: '/admin/products',
        },
        {
            title: 'Total Events',
            value: stats.events,
            icon: Calendar,
            color: 'bg-green-500',
            link: '/admin/events',
        },
        {
            title: 'Contact Messages',
            value: stats.contacts,
            icon: Mail,
            color: 'bg-purple-500',
            link: '/admin/contacts',
        },
    ];

    const quickActions = [
        {
            title: 'Add Product',
            description: 'Create a new product',
            icon: Package,
            link: '/admin/products/new',
            color: 'bg-earth-green',
        },
        {
            title: 'Add Event',
            description: 'Schedule a new event',
            icon: Calendar,
            link: '/admin/events/new',
            color: 'bg-olive',
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 mt-1">Welcome to Novatarra Admin Panel</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {statCards.map((stat) => {
                    const Icon = stat.icon;
                    return (
                        <Link
                            key={stat.title}
                            to={stat.link}
                            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                                    <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                                </div>
                                <div className={`${stat.color} p-3 rounded-lg`}>
                                    <Icon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <Link
                                key={action.title}
                                to={action.link}
                                className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow border-2 border-transparent hover:border-earth-green"
                            >
                                <div className="flex items-center space-x-4">
                                    <div className={`${action.color} p-3 rounded-lg`}>
                                        <Icon className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{action.title}</h3>
                                        <p className="text-sm text-gray-600">{action.description}</p>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Getting Started</h2>
                <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                        <TrendingUp className="h-5 w-5 text-earth-green mt-0.5" />
                        <div>
                            <p className="font-medium text-gray-900">Manage Products</p>
                            <p className="text-sm text-gray-600">Add, edit, or remove products from your catalog</p>
                        </div>
                    </div>
                    <div className="flex items-start space-x-3">
                        <TrendingUp className="h-5 w-5 text-earth-green mt-0.5" />
                        <div>
                            <p className="font-medium text-gray-900">Schedule Events</p>
                            <p className="text-sm text-gray-600">Create and manage workshops, seminars, and field days</p>
                        </div>
                    </div>
                    <div className="flex items-start space-x-3">
                        <TrendingUp className="h-5 w-5 text-earth-green mt-0.5" />
                        <div>
                            <p className="font-medium text-gray-900">View Messages</p>
                            <p className="text-sm text-gray-600">Check and respond to contact form submissions</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
